/**
 * AgentInspector — focal renderer for a Pane.
 * Shows manifest, state, last 20 actions from the tickets ledger,
 * pause/resume controls, and (for Service panes) an A2A "ask" form.
 */
import { useCallback, useEffect, useState } from 'react';
import * as api from '../lib/api';

interface Props {
  pane_id: string;
}

export function AgentInspector({ pane_id }: Props) {
  const [pane, setPane] = useState<api.PanePublic | null>(null);
  const [recent, setRecent] = useState<api.TicketPublic[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        api.panes.get(pane_id),
        api.tickets.list(50),
      ]);
      setPane(p);
      setRecent(
        t.tickets.filter((tk) => tk.specialist === pane_id).slice(0, 20),
      );
    } catch (e: unknown) {
      setErr((e as Error).message);
    }
  }, [pane_id]);

  useEffect(() => {
    load();
  }, [load]);

  async function pause() {
    setBusy(true);
    try {
      await api.panes.pause(pane_id);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    setBusy(true);
    try {
      await api.panes.resume(pane_id);
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (err) return <div className="text-bad text-xs">{err}</div>;
  if (!pane) return <div className="text-xs text-ink-mute">loading…</div>;
  return (
    <div className="space-y-3">
      <div className="card p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold">{pane.name}</div>
            <div className="text-[11px] text-ink-mute">id: {pane.id}</div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={
                pane.state === 'paused' || pane.state === 'PAUSED'
                  ? 'pill text-warn border-[#5c4720]'
                  : 'pill-sim'
              }
            >
              {String(pane.state).toUpperCase()}
            </span>
            <button className="btn text-xs" onClick={pause} disabled={busy}>
              pause
            </button>
            <button className="btn text-xs" onClick={resume} disabled={busy}>
              resume
            </button>
          </div>
        </div>
      </div>

      <div className="card p-3">
        <div className="text-[11px] text-ink-mute uppercase mb-2">manifest</div>
        <pre className="text-[11px] num text-ink-mute overflow-x-auto">
          {JSON.stringify(pane.manifest, null, 2)}
        </pre>
      </div>

      <div className="card p-3">
        <div className="text-[11px] text-ink-mute uppercase mb-2">
          last {recent.length} actions
        </div>
        {recent.length === 0 ? (
          <div className="text-xs text-ink-dim">no recent activity.</div>
        ) : (
          <ul className="space-y-1 text-xs">
            {recent.map((t) => (
              <li key={t.id} className="flex justify-between font-mono">
                <span className="truncate">
                  {t.action} <span className="text-ink-mute">conf={t.confidence.toFixed(2)}</span>
                </span>
                <span className="text-ink-mute">{t.created_at.split('T')[1]?.slice(0, 8)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pane_id === 'quant_math' && <QuantMathAsk />}
    </div>
  );
}

function QuantMathAsk() {
  const [skill, setSkill] = useState('position_sizing');
  const [paramsText, setParamsText] = useState(
    '{"account_value":100000,"risk_per_trade_pct":1,"entry_price":100,"stop_loss_price":97}',
  );
  const [out, setOut] = useState<unknown>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function ask() {
    setBusy(true);
    setErr(null);
    setOut(null);
    try {
      const params = JSON.parse(paramsText);
      const r = await api.panes.rpc('quant_math', skill, params);
      setOut(r);
    } catch (e: unknown) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card p-3">
      <div className="text-[11px] text-ink-mute uppercase mb-2">ask via A2A</div>
      <div className="flex gap-2 mb-2">
        <input
          className="input flex-1"
          value={skill}
          onChange={(e) => setSkill(e.target.value)}
        />
        <button className="btn" onClick={ask} disabled={busy}>
          {busy ? '…' : 'ask'}
        </button>
      </div>
      <textarea
        className="input w-full font-mono text-[11px]"
        rows={3}
        value={paramsText}
        onChange={(e) => setParamsText(e.target.value)}
      />
      {err && <div className="text-bad text-xs mt-2">{err}</div>}
      {out !== null && (
        <pre className="text-[11px] num text-ink-mute mt-2 overflow-x-auto">
          {JSON.stringify(out, null, 2)}
        </pre>
      )}
    </div>
  );
}
